import { Link } from 'react-router-dom';
import { Sparkles, Calendar, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const BOOKING_URL = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";

const treatments = [
  {
    name: 'HIFU Non-Surgical Face Lift',
    tagline: 'Lift, tighten & contour — without surgery.',
    description: 'Focused ultrasound energy targets the deep layers of your skin to stimulate collagen production. Tightens the jawline, lifts sagging cheeks, smooths wrinkles, and contours the neck. Walk-in, walk-out with zero downtime.',
    benefits: ['Immediate tightening effect', 'No needles or downtime', 'Results improve over 4-12 weeks', 'Face, neck & body treatments'],
    link: '/hifu',
    featured: true,
  },
  {
    name: 'Super Hair Removal (SHR)',
    tagline: 'Painless. Permanent. All skin types.',
    description: 'Next-generation laser hair removal with cooling-tip technology for virtually pain-free sessions. Safe for all skin types including darker and tanned skin. Say goodbye to shaving, waxing, and ingrown hairs for good.',
    benefits: ['Virtually painless treatment', 'Safe for all skin types', 'Fast sessions — large areas in minutes', 'Long-lasting results'],
    link: '/super-hair-removal',
    featured: false,
  },
  {
    name: 'Skin Pen Microneedling',
    tagline: 'The gold standard for skin rejuvenation.',
    description: 'Medical-grade microneedling creates controlled micro-injuries to stimulate your skin\'s natural healing response. Dramatically improves acne scars, enlarged pores, fine lines, and overall skin texture.',
    benefits: ['Acne scar reduction', 'Pore refinement', 'Collagen stimulation', 'Improved skin texture'],
    link: '/facials',
    featured: false,
  },
  {
    name: 'LED Light Therapy + Nervous System Reset',
    tagline: 'A high-tech escape for the modern mind.',
    description: 'Medical-grade Red & Near-Infrared LED wavelengths (660nm-850nm) boost collagen and erase dullness while our specialised protocol targets your vagus nerve for deep relaxation. Includes weighted eye mask and optional alpha-wave audio.',
    benefits: ['Vagus nerve stimulation', 'Visible "lit-from-within" glow', 'Lowers cortisol & stress', 'The "Botox effect" without needles'],
    link: '/facials',
    featured: false,
  },
  {
    name: 'Professional Chemical Peels',
    tagline: 'Reveal fresh, glowing skin beneath.',
    description: 'Professional-grade acids remove damaged outer layers to reveal brighter, smoother skin. Excellent for sun damage, pigmentation, uneven skin tone, and fine lines. Every peel is customised to your skin type.',
    benefits: ['Brightens skin tone', 'Reduces pigmentation', 'Smooths fine lines', 'Resurfaces sun damage'],
    link: '/facials',
    featured: false,
  },
  {
    name: 'Microdermabrasion',
    tagline: 'Instant polish for dull skin.',
    description: 'Deep vacuum exfoliation that clears blackheads, polishes dull skin, and reveals a high-glow finish immediately. Perfect as a standalone treatment or combined with other facials for enhanced results.',
    benefits: ['Instant results', 'Clears blackheads', 'No downtime', 'Suits most skin types'],
    link: '/facials',
    featured: false,
  },
  {
    name: 'Waterfall Head Spa',
    tagline: 'A full immersion into relaxation.',
    description: 'Our signature scalp treatment featuring warm rain-curtain therapy, deep scalp cleansing, and targeted massage. Detoxifies the scalp, promotes hair health, and delivers an unmatched level of relaxation.',
    benefits: ['Deep scalp detox', 'Promotes hair growth', 'Ultimate relaxation', 'Add-on LED & hydration options'],
    link: '/waterfall-head-spa',
    featured: false,
  },
];

const team = [
  { name: 'Deana', role: 'Executive Beauty Director', specialty: 'Skin Specialist', image: 'https://customer-assets.emergentagent.com/job_6b6596b0-1b37-4378-8699-89c151293ef2/artifacts/wuvji5lc_DEANA.png' },
  { name: 'Josie', role: 'Creative Director', specialty: 'Treatments & Care', image: 'https://customer-assets.emergentagent.com/job_d95e4940-e0b0-4d59-9b4c-c960d9d6604f/artifacts/8u4momme_PXL_20260227_044259811.RAW-01.COVER.jpg' },
];

export default function BeautyServices() {
  return (
    <main className="min-h-screen bg-[#FFFDF9]" data-testid="beauty-services-page">
      <SEO page="beauty" />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32" data-testid="beauty-hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_1dc100c1-fd6e-4e96-8e6a-366ccfb0323b/artifacts/grryzocs_skincare%20lab.jpg"
            alt="Beauty & skin treatments at Artistry House"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFDF9] via-[#FFFDF9]/95 to-[#FFFDF9]/70" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#D4C4B0]/20 border border-[#D4C4B0]/50 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-[#8D7B68]" />
              <span className="text-sm text-[#5D4A3A]">Upstairs Sanctuary</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#2C2420] leading-[1.1] mb-6">
              Beauty & Skin Services
            </h1>
            <p className="text-lg text-[#5D4A3A] leading-relaxed mb-4">
              Transformative skin treatments, non-surgical lifting, painless hair removal, and our signature Waterfall Head Spa — all in our newly renovated upstairs beauty sanctuary.
            </p>
            <p className="text-base text-[#8D7B68] italic mb-8">
              New clients: Start with a Discovery Skin Consultation & Facial — your personalised skin roadmap.
            </p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-8 py-4 text-lg" data-testid="beauty-book-btn">
                <Calendar className="w-5 h-5 mr-2" />
                Book Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Quick Overview Stats */}
      <section className="py-12 bg-[#2C2420]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <p className="text-2xl font-medium mb-1">7+</p>
              <p className="text-sm text-[#BCAAA4]">Treatments Available</p>
            </div>
            <div>
              <p className="text-2xl font-medium mb-1">Aspect</p>
              <p className="text-sm text-[#BCAAA4]">Skincare Products</p>
            </div>
            <div>
              <p className="text-2xl font-medium mb-1">Medical</p>
              <p className="text-sm text-[#BCAAA4]">Grade Technology</p>
            </div>
            <div>
              <p className="text-2xl font-medium mb-1">Custom</p>
              <p className="text-sm text-[#BCAAA4]">Treatment Plans</p>
            </div>
          </div>
        </div>
      </section>

      {/* All Treatments */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">What We Do</p>
            <h2 className="text-3xl md:text-4xl font-medium text-[#2C2420] mb-4">Our Treatments</h2>
            <p className="text-base md:text-lg text-[#5D4A3A] max-w-2xl mx-auto">
              Every treatment is tailored to your unique skin concerns after a thorough consultation.
            </p>
          </div>

          <div className="space-y-6">
            {treatments.map((treatment) => (
              <div
                key={treatment.name}
                className={`rounded-lg border transition-all hover:shadow-md ${
                  treatment.featured
                    ? 'bg-[#8D7B68]/5 border-[#8D7B68]'
                    : 'bg-white border-[#D4C4B0]/50 hover:border-[#8D7B68]/50'
                }`}
              >
                <div className="p-6 md:p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                    {/* Left: Name & Description */}
                    <div className="lg:col-span-7">
                      {treatment.featured && (
                        <span className="inline-block text-xs uppercase tracking-widest text-[#8D7B68] font-medium mb-2">Most Popular</span>
                      )}
                      <h3 className="text-xl md:text-2xl font-medium text-[#2C2420] mb-1">{treatment.name}</h3>
                      <p className="text-sm text-[#8D7B68] italic mb-3">{treatment.tagline}</p>
                      <p className="text-sm text-[#5D4A3A] leading-relaxed">{treatment.description}</p>
                    </div>

                    {/* Right: Benefits + Link */}
                    <div className="lg:col-span-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                        {treatment.benefits.map((b) => (
                          <div key={b} className="flex items-center gap-2">
                            <Check className="w-3.5 h-3.5 text-[#4A5D44] flex-shrink-0" />
                            <span className="text-xs text-[#2C2420]">{b}</span>
                          </div>
                        ))}
                      </div>
                      <Link to={treatment.link} className="inline-flex items-center gap-1 text-sm text-[#8D7B68] hover:text-[#6B5D4D] font-medium transition-colors">
                        Learn more <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="py-20 md:py-28 bg-[#E8DED2]/30" data-testid="beauty-team-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Meet The Experts</p>
            <h2 className="text-3xl md:text-4xl font-medium text-[#2C2420] mb-4">Our Beauty Team</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {team.map((member) => (
              <div key={member.name} className="text-center">
                <div className="aspect-[3/4] rounded-lg overflow-hidden mb-4">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-lg font-medium text-[#2C2420]">{member.name}</h4>
                <p className="text-sm text-[#8D7B68]">{member.role}</p>
                <p className="text-xs text-[#6B5D4D] mt-1">{member.specialty}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#2C2420]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-medium text-white mb-4">
            Not Sure Where to Start?
          </h2>
          <p className="text-lg text-[#BCAAA4] max-w-2xl mx-auto mb-8">
            Book a Discovery Skin Consultation & Facial. Our skin specialists will analyse your skin and create a personalised treatment plan — it's the perfect first step.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-10 py-6 text-lg">
              <Calendar className="w-5 h-5 mr-2" />
              Book Your Discovery Facial
            </Button>
          </a>
        </div>
      </section>
    </main>
  );
}
